import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { fmtDate } from "../lib/format";
import { Card } from "../components/shared";
import { AlertTriangle, CalendarClock, BellOff } from "lucide-react";

const TIPO_LABEL = {
  vencido: "Vigencia vencida", por_vencer: "Próximo a vencer",
  compromiso_incumplido: "Compromiso incumplido", compromiso_proximo: "Compromiso próximo",
};

export default function Alertas() {
  const { pid } = useParams();
  const [alertas, setAlertas] = useState(null);

  useEffect(() => {
    setAlertas(null);
    api.get(`/properties/${pid}/alertas`).then((r) => setAlertas(r.data));
  }, [pid]);

  if (!alertas) return <div className="p-10 text-slate-400 animate-pulse">Cargando alertas...</div>;
  const rojas = alertas.filter((a) => a.severidad === "rojo");
  const amarillas = alertas.filter((a) => a.severidad === "amarillo");

  const Item = ({ a, i }) => (
    <motion.div initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 * i }}>
      <Link to={`/p/${pid}/tramites/${a.tramite_id}`} data-testid={`alerta-${a.id}`}
        className={`flex items-center gap-4 p-4 rounded-lg border bg-white hover:shadow-md transition-shadow ${a.severidad === "rojo" ? "border-red-200" : "border-amber-200"}`}>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${a.severidad === "rojo" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}>
          {a.tipo.startsWith("compromiso") ? <CalendarClock size={18} /> : <AlertTriangle size={18} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-800 truncate">{a.tramite_nombre}</p>
          <p className="text-xs text-slate-500">{TIPO_LABEL[a.tipo]} · {a.autoridad || "—"}{a.upe_clave ? ` · ${a.upe_clave}` : ""}</p>
        </div>
        <div className="text-right">
          <p className={`text-sm font-bold ${a.severidad === "rojo" ? "text-red-600" : "text-amber-600"}`}>{a.mensaje}</p>
          <p className="text-xs text-slate-400">{fmtDate(a.fecha)}</p>
        </div>
      </Link>
    </motion.div>
  );

  return (
    <div className="p-4 sm:p-8 max-w-[900px]">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <p className="text-xs tracking-[0.25em] uppercase text-[#00A3B5] font-bold mb-1">Alertas automáticas a 60/30/15/7 días</p>
        <h1 className="font-display text-3xl font-bold text-[#002B49]">Bandeja de Alertas</h1>
      </motion.div>
      {alertas.length === 0 ? (
        <Card className="p-12 text-center text-slate-400" testId="sin-alertas">
          <BellOff size={32} className="mx-auto mb-3 text-slate-300" />
          <p>Sin alertas vigentes. Todos los vencimientos y compromisos están bajo control.</p>
        </Card>
      ) : (
        <div className="space-y-6">
          {rojas.length > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-red-600 mb-3">Severidad alta ({rojas.length})</h3>
              <div className="space-y-2">{rojas.map((a, i) => <Item key={a.id} a={a} i={i} />)}</div>
            </div>
          )}
          {amarillas.length > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-3">Severidad media ({amarillas.length})</h3>
              <div className="space-y-2">{amarillas.map((a, i) => <Item key={a.id} a={a} i={i} />)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
